import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleIcon from '@mui/icons-material/Article';
import BuildIcon from '@mui/icons-material/Build';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatIcon from '@mui/icons-material/Chat';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExtensionIcon from '@mui/icons-material/Extension';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SearchIcon from '@mui/icons-material/Search';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Box,
  Typography,
  Button,
  Drawer,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';

import {
  HelpArticle,
  HelpCategory,
  HelpCenterOverview,
  getHelpOverview,
  getArticlesByCategory,
  searchHelpArticles,
  getHelpArticleBySlug,
} from '../../api/helpCenterAPI';
import { useAuth } from '../../context/AuthContext';

// Drawer width constant
const HELP_DRAWER_WIDTH = 400;

// Icon component mapping
const IconMap: Record<string, React.ReactNode> = {
  rocket_launch: <RocketLaunchIcon />,
  people: <PeopleIcon />,
  calendar_month: <CalendarMonthIcon />,
  chat: <ChatIcon />,
  videocam: <VideocamIcon />,
  payments: <PaymentsIcon />,
  extension: <ExtensionIcon />,
  build: <BuildIcon />,
  help_outline: <HelpOutlineIcon />,
};

type ViewType = 'overview' | 'category' | 'article' | 'search';

interface HelpCenterDrawerProps {
  open: boolean;
  onClose: () => void;
}

const HelpCenterDrawer: React.FC<HelpCenterDrawerProps> = ({ open, onClose }) => {
  const theme = useTheme();
  // HA-285: filter the catalog for Client viewers. The role is
  // forwarded to every static-data accessor so categories,
  // popular articles, and search results all honor the
  // whitelist + patient-perspective overrides.
  const { currentUser } = useAuth();
  const role = currentUser?.role;

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<HelpCenterOverview | null>(null);
  const [currentView, setCurrentView] = useState<ViewType>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<HelpArticle[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [categoryArticles, setCategoryArticles] = useState<HelpArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    const data = await getHelpOverview(role);

    setOverview(data);
    setLoading(false);
  }, [role]);

  useEffect(() => {
    if (open && !overview) {
      fetchOverview();
    }
  }, [open, overview, fetchOverview]);

  // HA-285: clear cached overview if role flips mid-session so a
  // Practitioner→Client (impersonation, role-switch testing) flow
  // doesn't leak the practitioner catalog.
  useEffect(() => {
    setOverview(null);
  }, [role]);

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setCurrentView('overview');
      setSearchResults([]);

      return;
    }

    setLoading(true);
    const results = await searchHelpArticles(query, undefined, undefined, role);

    if (results) {
      setSearchResults(results.articles);
      setSearchTotal(results.totalResults);
    }
    setCurrentView('search');
    setLoading(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery);
    }
  };

  const handleCategoryClick = async (category: HelpCategory) => {
    setLoading(true);
    setSelectedCategory(category);
    const articles = await getArticlesByCategory(category.categorySlug, role);

    setCategoryArticles(articles);
    setCurrentView('category');
    setLoading(false);
  };

  const handleArticleClick = async (article: HelpArticle) => {
    setLoading(true);
    const fullArticle = await getHelpArticleBySlug(article.slug);

    setSelectedArticle(fullArticle || article);
    setCurrentView('article');
    setLoading(false);
  };

  const handleBack = () => {
    if (currentView === 'article' && selectedCategory) {
      setCurrentView('category');
      setSelectedArticle(null);
    } else if (currentView === 'article' && searchResults.length > 0) {
      setCurrentView('search');
      setSelectedArticle(null);
    } else {
      setCurrentView('overview');
      setSelectedCategory(null);
      setSelectedArticle(null);
      setSearchResults([]);
      setSearchQuery('');
    }
  };

  const getCategoryIcon = (iconName?: string) => {
    return IconMap[iconName || ''] || <HelpOutlineIcon />;
  };

  // Styles for the drawer
  const styles = {
    drawer: {
      '& .MuiDrawer-paper': {
        width: HELP_DRAWER_WIDTH,
        maxWidth: '100vw',
      },
    },
    drawerContent: {
      display: 'flex',
      flexDirection: 'column' as const,
      height: '100%',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
      color: theme.palette.info.contrastText,
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    searchSection: {
      padding: theme.spacing(2, 2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[50],
    },
    searchInput: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(3),
      },
    },
    content: {
      flex: 1,
      overflowY: 'auto' as const,
      padding: theme.spacing(2, 2.5),
    },
    categoryGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(3),
    },
    categoryCard: {
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.primary.main,
      },
    },
    categoryIcon: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.primary.light + '30',
      color: theme.palette.primary.main,
      flexShrink: 0,
      '& svg': {
        fontSize: 20,
      },
    },
    categoryInfo: {
      flex: 1,
      minWidth: 0,
    },
    categoryName: {
      fontWeight: 600,
      fontSize: '0.9rem',
    },
    categoryDescription: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
    },
    categoryCount: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
    },
    sectionTitle: {
      fontWeight: 600,
      fontSize: '0.875rem',
      marginBottom: theme.spacing(1.5),
    },
    articleList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(1),
    },
    articleCard: {
      padding: theme.spacing(1.5),
      borderRadius: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.primary.main,
      },
    },
    articleTitle: {
      fontWeight: 600,
      fontSize: '0.85rem',
      marginBottom: theme.spacing(0.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    articleSummary: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.5),
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden',
    },
    articleMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
    },
    articleDetail: {
      '& h1, & h2, & h3, & h4': {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontSize: '1rem',
      },
      '& p': {
        marginBottom: theme.spacing(1.5),
        lineHeight: 1.6,
        fontSize: '0.875rem',
      },
      '& ul, & ol': {
        marginBottom: theme.spacing(1.5),
        paddingLeft: theme.spacing(2.5),
      },
      '& li': {
        marginBottom: theme.spacing(0.5),
        fontSize: '0.875rem',
      },
      '& code': {
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(0.25, 0.5),
        borderRadius: theme.spacing(0.5),
        fontFamily: 'monospace',
        fontSize: '0.8em',
      },
      '& pre': {
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(1.5),
        borderRadius: theme.spacing(1),
        overflowX: 'auto' as const,
        fontSize: '0.8rem',
        '& code': {
          backgroundColor: 'transparent',
          padding: 0,
        },
      },
    },
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      flexWrap: 'wrap' as const,
    },
    breadcrumbLink: {
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
      },
    },
    backButton: {
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(0.5, 1),
      fontSize: '0.8rem',
    },
    tag: {
      fontSize: '0.65rem',
      height: 18,
      marginRight: theme.spacing(0.5),
    },
    noResults: {
      textAlign: 'center' as const,
      padding: theme.spacing(3),
      color: theme.palette.text.secondary,
    },
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    articlesSection: {
      marginTop: theme.spacing(3),
    },
    searchResultsHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.5),
    },
  };

  const renderOverview = () => (
    <Box>
      <Typography variant="subtitle2" sx={styles.sectionTitle} gutterBottom>
        Browse by Category
      </Typography>
      <Box sx={styles.categoryGrid}>
        {overview?.categories?.map((category) => (
          <Box
            key={category.categorySlug}
            sx={styles.categoryCard}
            onClick={() => handleCategoryClick(category)}
          >
            <Box sx={styles.categoryIcon}>{getCategoryIcon(category.icon)}</Box>
            <Box sx={styles.categoryInfo}>
              <Typography sx={styles.categoryName}>{category.name}</Typography>
              <Typography sx={styles.categoryDescription}>{category.description}</Typography>
              <Typography sx={styles.categoryCount}>{category.articleCount} articles</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {overview?.popularArticles && overview.popularArticles.length > 0 && (
        <Box sx={styles.articlesSection}>
          <Typography variant="subtitle2" sx={styles.sectionTitle} gutterBottom>
            Popular Articles
          </Typography>
          <Box sx={styles.articleList}>
            {overview.popularArticles.map((article) => (
              <Box
                key={article.articleId}
                sx={styles.articleCard}
                onClick={() => handleArticleClick(article)}
              >
                <Typography sx={styles.articleTitle}>
                  <ArticleIcon fontSize="small" color="action" />
                  {article.title}
                </Typography>
                <Typography sx={styles.articleSummary}>{article.summary}</Typography>
                <Box sx={styles.articleMeta}>
                  <Chip label={article.category} size="small" sx={styles.tag} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderCategory = () => (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={styles.backButton}
        color="inherit"
        size="small"
      >
        Back
      </Button>

      <Box sx={styles.breadcrumb}>
        <Typography
          component="span"
          sx={styles.breadcrumbLink}
          onClick={() => setCurrentView('overview')}
        >
          Help Center
        </Typography>
        <ChevronRightIcon fontSize="small" />
        <Typography component="span">{selectedCategory?.name}</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={styles.categoryIcon}>{getCategoryIcon(selectedCategory?.icon)}</Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
            }}
          >
            {selectedCategory?.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.8rem',
            }}
          >
            {selectedCategory?.description}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.articleList}>
        {categoryArticles.map((article) => (
          <Box
            key={article.articleId}
            sx={styles.articleCard}
            onClick={() => handleArticleClick(article)}
          >
            <Typography sx={styles.articleTitle}>
              <ArticleIcon fontSize="small" color="action" />
              {article.title}
            </Typography>
            <Typography sx={styles.articleSummary}>{article.summary}</Typography>
            {article.tags && article.tags.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {article.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" sx={styles.tag} />
                ))}
              </Box>
            )}
          </Box>
        ))}
        {categoryArticles.length === 0 && (
          <Box sx={styles.noResults}>
            <Typography variant="body2">No articles found in this category.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderArticle = () => (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={styles.backButton}
        color="inherit"
        size="small"
      >
        Back
      </Button>

      <Box sx={styles.breadcrumb}>
        <Typography
          component="span"
          sx={styles.breadcrumbLink}
          onClick={() => setCurrentView('overview')}
        >
          Help
        </Typography>
        <ChevronRightIcon fontSize="small" />
        {selectedCategory && (
          <>
            <Typography
              component="span"
              sx={styles.breadcrumbLink}
              onClick={() => setCurrentView('category')}
            >
              {selectedCategory.name}
            </Typography>
            <ChevronRightIcon fontSize="small" />
          </>
        )}
        <Typography
          component="span"
          sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {selectedArticle?.title}
        </Typography>
      </Box>

      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontWeight: 700,
          fontSize: '1.1rem',
        }}
      >
        {selectedArticle?.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={selectedArticle?.category} size="small" color="primary" sx={styles.tag} />
      </Box>

      <Box sx={styles.articleDetail}>
        <ReactMarkdown>{selectedArticle?.content || ''}</ReactMarkdown>
      </Box>

      {selectedArticle?.tags && selectedArticle.tags.length > 0 && (
        <Box sx={{ mt: 3, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography
            variant="caption"
            gutterBottom
            sx={{
              color: 'text.secondary',
            }}
          >
            Related Topics
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {selectedArticle.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5, ...styles.tag }} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderSearchResults = () => (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={styles.backButton}
        color="inherit"
        size="small"
      >
        Back
      </Button>

      <Box sx={styles.searchResultsHeader}>
        <Typography variant="subtitle2">
          {searchTotal} result{searchTotal !== 1 ? 's' : ''} for &quot;{searchQuery}&quot;
        </Typography>
        <Button
          size="small"
          onClick={() => {
            setSearchQuery('');
            setSearchResults([]);
            setCurrentView('overview');
          }}
        >
          Clear
        </Button>
      </Box>

      <Box sx={styles.articleList}>
        {searchResults.map((article) => (
          <Box
            key={article.articleId}
            sx={styles.articleCard}
            onClick={() => handleArticleClick(article)}
          >
            <Typography sx={styles.articleTitle}>
              <ArticleIcon fontSize="small" color="action" />
              {article.title}
            </Typography>
            <Typography sx={styles.articleSummary}>{article.summary}</Typography>
            <Box sx={styles.articleMeta}>
              <Chip label={article.category} size="small" sx={styles.tag} />
            </Box>
          </Box>
        ))}
        {searchResults.length === 0 && (
          <Box sx={styles.noResults}>
            <Typography variant="subtitle2" gutterBottom>
              No results found
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              Try different keywords or browse by category.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={styles.drawer}
      aria-label="Help Center"
    >
      <Box sx={styles.drawerContent}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box sx={styles.headerTitle}>
            <HelpOutlineIcon />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
              }}
            >
              Help Center
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'inherit' }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search */}
        <Box sx={styles.searchSection}>
          <TextField
            fullWidth
            placeholder="Search help articles..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            sx={styles.searchInput}
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => handleSearch(searchQuery)}>
                      Search
                    </Button>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={styles.content}>
          {loading ? (
            <Box sx={styles.loading}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {currentView === 'overview' && renderOverview()}
              {currentView === 'category' && renderCategory()}
              {currentView === 'article' && renderArticle()}
              {currentView === 'search' && renderSearchResults()}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default HelpCenterDrawer;
